import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { asDict, useHttp } from '@climate/components'
import type { CreditRating, CreditRatingAgency, CreditRatingsAgenciesGrouped } from '../types'

const ENDPOINT = '/api/v1/credit_ratings'

export const useCreditRatingsService = () => {
  const http = useHttp()

  const { data: creditRatings = {} } = useQuery<Record<number, string>>({
    queryKey: ['creditRatings'],
    queryFn: async (): Promise<Record<number, string>> => {
      const data = await http.apiGet(ENDPOINT) as CreditRating[]
      return asDict(data, (e) => (`(${e.value}) ${e.name}`))
    },
  })

  const { data: creditRatingsAgencies = [] } = useQuery<CreditRatingAgency[]>({
    queryKey: ['creditRatingsAgencies'],
    queryFn: () => http.apiGet(`${ENDPOINT}/agencies`) as Promise<CreditRatingAgency[]>,
  })

  const creditRatingsAgenciesGrouped: CreditRatingsAgenciesGrouped = useMemo(() =>
    creditRatingsAgencies.reduce<CreditRatingsAgenciesGrouped>((result, item) => {
      if (result[item.agency]) {
        result[item.agency].push({ value: item.id, label: item.rating })
      } else {
        result[item.agency] = [{ value: item.id, label: item.rating }]
      }
      return result
    }, {}),
  [creditRatingsAgencies]
  )

  return {
    creditRatings,
    creditRatingsAgencies,
    creditRatingsAgenciesGrouped,
  }
}
